import { availabilitiesAtom, itemsAtom, optionGroupsAtom } from "@/atoms";
import { activeBenefitsAtom } from "@/atoms/active-benefits";
import { ItemOfferPrice } from "@/components/home";
import {
  MENU_CARD_MIN_HEIGHT_REM,
  MENU_IMAGE_ASPECT,
  MENU_IMAGE_SIZE_REM,
  STOREFRONT_ROUNDED_PX,
  isStackedShape,
} from "@/domain/storefront-appearance";
import { useStorefrontAppearance } from "@/hooks/use-storefront-appearance";
import { cn } from "@/lib/cn";
import { formatBRL } from "@/lib/format-brl";
import { triggerItemBenefit } from "@/lib/loyalty-benefits";
import { useOptimisticCart } from "@/hooks/use-optimistic-cart";
import { useStoreClosedGuard } from "@/hooks/use-store-closed-guard";
import { calcMinGroupPrice } from "@/lib/calc-min-group-price";
import { computeItemTotal } from "@/lib/pricing";
import { minuteNowAtom, tenantTimezoneAtom } from "@/atoms/tenant-clock";
import { isAvailableNow } from "@/lib/is-available-now";
import { FLY_ORIGIN_ATTR, emitCartBump, flyToCart } from "@/lib/fly-to-cart";
import { CartItem, Item, ItemOffer, Status } from "@/types";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { v4 as uuid } from "uuid";
import PromotionalRibbon from "../PromotionalRibbon";
import { Image } from "../image";
import { NoImage } from "../no-image/NoImage";
import { Badge, Card, QuantityStepper, Text } from "@/components/ui";

export default function ItemOfferCard({
  itemOffer,
  status,
  disabled,
  handleItemClick,
  itemId,
  hideQuantityButton = false,
}: {
  itemOffer: ItemOffer;
  disabled: boolean;
  status: Status;
  index: number;
  itemOffers: ItemOffer[];
  handleItemClick: (itemId: string) => void;
  itemId: string;
  /** Indoor digital menu: info-only card, without the quantity overlay. */
  hideQuantityButton?: boolean;
}) {
  const items = useAtomValue(itemsAtom);
  const optionGroups = useAtomValue(optionGroupsAtom);
  const availability = useAtomValue(availabilitiesAtom);
  const timeZone = useAtomValue(tenantTimezoneAtom);
  const now = useAtomValue(minuteNowAtom);
  const { cardSize, itemImage, menu } = useStorefrontAppearance();
  const stacked = itemImage.visible && isStackedShape(itemImage.shape);

  const item = useMemo(() => items.find((v) => v.id === itemOffer.itemId), [items, itemOffer.itemId]);

  const activeBenefits = useAtomValue(activeBenefitsAtom);
  const triggerBenefit = useMemo(
    () => triggerItemBenefit(activeBenefits, item?.externalCode, item?.id ?? itemOffer.itemId),
    [activeBenefits, item?.externalCode, item?.id, itemOffer.itemId]
  );

  const inUseOptionGroups = useMemo(
    () => optionGroups.filter((opg) => itemOffer.optionGroupsId && itemOffer.optionGroupsId.includes(opg.id)),
    [optionGroups, itemOffer.optionGroupsId]
  );

  const minPrice = useMemo(
    () => inUseOptionGroups.reduce((acc, opg) => acc + calcMinGroupPrice(opg, "value"), 0) + itemOffer.price.value,
    [inUseOptionGroups, itemOffer.price.value]
  );

  const minPriceIfDiscounted = useMemo(
    () =>
      inUseOptionGroups.reduce((acc, opg) => acc + calcMinGroupPrice(opg, "originalValue"), 0) +
      (itemOffer.price.originalValue ?? 0),
    [inUseOptionGroups, itemOffer.price.originalValue]
  );

  const isAvailable = useMemo(
    () => isAvailableNow(itemOffer.availabilityId, availability, now, timeZone),
    [availability, itemOffer.availabilityId, timeZone, now]
  );

  const discountPercent = useMemo(() => {
    const hasOptionGroups = itemOffer.optionGroupsId && itemOffer.optionGroupsId.length > 0;
    if (hasOptionGroups && minPriceIfDiscounted > minPrice && minPriceIfDiscounted > 0) {
      return Math.round(((minPriceIfDiscounted - minPrice) / minPriceIfDiscounted) * 100);
    }
    if (itemOffer.price.originalValue && itemOffer.price.originalValue > itemOffer.price.value) {
      return Math.round(
        ((itemOffer.price.originalValue - itemOffer.price.value) / itemOffer.price.originalValue) * 100
      );
    }
    return 0;
  }, [itemOffer, minPrice, minPriceIfDiscounted]);

  const onItemClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    handleItemClick(itemId);
  };

  if (!item) return null;

  const dimmed = !isAvailable || disabled;

  return (
    <>
      <div
        onClick={onItemClick}
        data-testid={`item-offer-card-${itemOffer.id}`}
        className={cn("block h-full cursor-pointer", {
          "pointer-events-none cursor-not-allowed": disabled || !isAvailable,
          "opacity-100": status === "UNAVAILABLE",
        })}
        aria-disabled={disabled || !isAvailable}
        tabIndex={disabled || !isAvailable ? -1 : undefined}
      >
        <Card
          padding="sm"
          style={{
            minHeight: `${MENU_CARD_MIN_HEIGHT_REM[cardSize]}rem`,
            borderRadius: `${STOREFRONT_ROUNDED_PX[menu.rounded]}px`,
          }}
          className={cn(
            "relative flex w-full gap-3",
            stacked ? "flex-col items-stretch" : "items-center"
          )}
        >
          {itemImage.visible && (
            <div
              className={cn("relative", stacked ? "w-full" : "shrink-0")}
              style={
                stacked
                  ? { aspectRatio: MENU_IMAGE_ASPECT.wide }
                  : {
                      width: `${MENU_IMAGE_SIZE_REM[itemImage.size]}rem`,
                      height: `${MENU_IMAGE_SIZE_REM[itemImage.size]}rem`,
                    }
              }
              {...{ [FLY_ORIGIN_ATTR]: "" }}
            >
              <div
                className={cn(
                  "overflow-hidden",
                  stacked ? "absolute inset-0" : "relative h-full w-full",
                  itemImage.shape === "round" ? "rounded-full" : "rounded-card"
                )}
              >
                <PromotionalRibbon
                  isPromotional={itemOffer.isPromotional}
                  text={itemOffer.promotionalBannerText}
                  size="sm"
                />
                {discountPercent > 0 && isAvailable && !disabled && (
                  <Badge variant="solid" tone="danger" size="lg" elevation="card" className="absolute left-1 top-1 z-raised">
                    -{discountPercent}%
                  </Badge>
                )}
                {item.image?.URL ? (
                  <Image
                    width={200}
                    height={200}
                    alt={item.name}
                    src={item.image?.URL}
                    crc={item.image?.["CRC-32"]}
                    sizes="128px"
                    fit="contain"
                    className={cn("h-full w-full", dimmed && "opacity-50")}
                  />
                ) : (
                  <NoImage width={200} height={200} className="h-full w-full object-cover" />
                )}
              </div>
              {!hideQuantityButton && itemOffer.optionGroupsId && itemOffer.optionGroupsId.length === 0 && (
                <div className="absolute inset-x-1 bottom-1 flex justify-end">
                  <ItemQuantityButton itemOffer={itemOffer} item={item} />
                </div>
              )}
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col justify-start">
            <Text
              size="lg"
              weight="bold"
              leading="tight"
              clamp={2}
              tone={dimmed ? "disabled" : "default"}
              className={cn(dimmed && "line-through")}
            >
              {item.name}
            </Text>
            {triggerBenefit && (
              <Badge tone="primary" className="mt-1.5 max-w-full flex-wrap gap-x-1 gap-y-0 whitespace-normal">
                {triggerBenefit.label}
                {triggerBenefit.minSpend > 0 && (
                  <Text as="span" weight="normal">
                    a partir de {formatBRL(triggerBenefit.minSpend)}
                  </Text>
                )}
              </Badge>
            )}
            <Text
              tone={dimmed ? "disabled" : "muted"}
              clamp={1}
              className={cn("mt-1.5", dimmed && "line-through")}
            >
              {item.description}
            </Text>
            <div className={cn("flex items-center justify-between", dimmed && "line-through opacity-50")}>
              <div className="w-full flex flex-col">
                <ItemOfferPrice
                  price={itemOffer.price}
                  minPrice={minPrice}
                  optionGroups={inUseOptionGroups}
                  isOptionGroup={itemOffer.optionGroupsId && itemOffer.optionGroupsId.length > 0}
                  minPriceIfDiscounted={minPriceIfDiscounted}
                  dense={cardSize === "compact"}
                />
              </div>
            </div>
            {dimmed && (
              <Badge variant="outline" size="flush" className="mt-2">
                Indisponível
              </Badge>
            )}
          </div>
          {!itemImage.visible &&
            !hideQuantityButton &&
            itemOffer.optionGroupsId &&
            itemOffer.optionGroupsId.length === 0 && (
              <div className="absolute bottom-3 right-3 flex w-32 justify-end" {...{ [FLY_ORIGIN_ATTR]: "" }}>
                <ItemQuantityButton itemOffer={itemOffer} item={item} />
              </div>
            )}
        </Card>
      </div>
    </>
  );
}

function ItemQuantityButton({ itemOffer, item }: { itemOffer: ItemOffer; item: Item }) {
  const { cart, addItem, updateItemQuantity, removeItem } = useOptimisticCart();
  const { merchantIsOpen, ensureStoreOpen } = useStoreClosedGuard();

  const quantity = useMemo(() => {
    if (!merchantIsOpen) return 0;
    return cart.items.find((i) => i.itemOffer.id === itemOffer.id)?.quantity || 0;
  }, [cart.items, itemOffer.id, merchantIsOpen]);

  function handleAddItem(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    event.preventDefault();

    if (!ensureStoreOpen()) return;

    flyToCart(event.currentTarget.closest(`[${FLY_ORIGIN_ATTR}]`));

    const matchingItems = cart.items.filter((i) => i.itemOffer.id === itemOffer.id);
    const itemWithoutInstructions = matchingItems.find((i) => i.specialInstructions === "");

    if (itemWithoutInstructions) {
      updateItemQuantity(itemWithoutInstructions.id, itemWithoutInstructions.quantity + 1);
    } else {
      const unitPrice = computeItemTotal(itemOffer, [], {});
      const newItem: CartItem = {
        id: uuid(),
        item: item,
        itemOffer: itemOffer,
        itemOptions: {},
        optionGroups: [],
        optionQuantities: {},
        quantity: 1,
        itemTotalPrice: unitPrice,
        unitPrice: unitPrice,
        specialInstructions: "",
      };

      addItem(newItem);
    }
  }

  const handleRemoveItem = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    const matchingItems = cart.items.filter((i) => i.itemOffer.id === itemOffer.id);
    const itemWithoutInstructions = matchingItems.find((i) => i.specialInstructions === "");

    if (itemWithoutInstructions) {
      if (itemWithoutInstructions.quantity === 1) {
        removeItem(itemWithoutInstructions.id);
      } else {
        updateItemQuantity(itemWithoutInstructions.id, itemWithoutInstructions.quantity - 1);
      }
      emitCartBump("remove");
    }
  };

  return (
    <QuantityStepper
      value={quantity}
      variant="floating"
      collapsible
      tone="secondary"
      onIncrement={handleAddItem}
      onDecrement={handleRemoveItem}
      incrementProps={{ "data-testid": `item-offer-qty-increase-${itemOffer.id}` }}
      decrementProps={{ "data-testid": `item-offer-qty-decrease-${itemOffer.id}` }}
      valueProps={{ "data-testid": `item-offer-qty-${itemOffer.id}` }}
    />
  );
}
